<template>
  <div
    v-if="layer"
    class="kd-layer"
    :class="{
      'kd-layer--is-active': isActiveLayer,
      'kd-layer--no-highlight': LAYOUT_MODE_ACTIVE,  
    }"
    @click="onLayerClick(layer.id)"
    ref="layer"
    :style="layerStyle"
    v-click-outside="clickOutsideConfig"
  >
    <kd-layer-text
      v-if="layer.type === 'text'"
      :layer="layer"
    ></kd-layer-text>
    <kd-layer-url
      v-if="layer.type === 'url'"
      :orientation="layer.orientation"
      :url="SHOP_URL"
    ></kd-layer-url>
    <kd-layer-foto
      v-if="layer.type === 'foto'"
      :layer="layer"
    ></kd-layer-foto>
    <kd-layer-grafik
      v-if="layer.type === 'grafik'"
      :layer="layer"
    ></kd-layer-grafik>
  </div>
</template>

<script>
import * as log from 'loglevel';
import { mapGetters, mapMutations, mapActions } from 'vuex';

import KdLayerText from '@/components/KdLayerText.vue';
import KdLayerFoto from '@/components/KdLayerFoto.vue';
import KdLayerGrafik from '@/components/KdLayerGrafik.vue';
import KdLayerUrl from '@/components/KdLayerUrl.vue';

import domHelpers from '@/shared/dom-helpers';

import {
  ACTIVE_LAYER_ID,
  SHOP_URL,
  LAYOUT_MODE_ACTIVE,
} from '@/store/getter-types';

import {
  SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT,
  SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT_TO_NONE,
} from '@/store/mutation-types';

import {
  ACTIVATE_LAYER_BY_ID,
  DEACTIVATE_LAYER,
} from '@/store/action-types';

export default {
  props: {
    layer: Object,
  },
  data() {
    return {
      isActiveLayer: false,
      clickOutsideConfig: {
        handler: this.onClickOutside,
        middleware: this.shouldDeactivateLayer,
        events: ['dblclick', 'click', 'touchstart'],
      },
    };
  },
  computed: {
    ...mapGetters([
      ACTIVE_LAYER_ID,
      SHOP_URL,
      LAYOUT_MODE_ACTIVE,
    ]),
    isInteractiveLayer() {
      return this.layer.type === 'foto' || this.layer.type === 'text';
    },
    layerStyle() {
      return {
        top: `${this.layer.y}%`,
        left: `${this.layer.x}%`,
        width: `${this.layer.width}%`,
        height: `${this.layer.height}%`,
        /* grafik-layers are not supposed to be edited / selected by the user
           TODO: pointer events might need to be reenabled, and return false,
           because of click outside
        */
        pointerEvents: this.isInteractiveLayer && !this.LAYOUT_MODE_ACTIVE ? 'all' : 'none',
      };
    },
    /*
     * The approch would be nicer than using the boolean "isActiveLayer" in data,
     * but resulted in a reactivity problem (ACTIVE_LAYER_ID was not reactive).
    */
    // isActiveLayer() {
    //   return (this.layer && this.layer.id === ACTIVE_LAYER_ID);
    // },
  },
  methods: {
    ...mapMutations([
      SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT,
      SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT_TO_NONE,
    ]),
    ...mapActions([
      ACTIVATE_LAYER_BY_ID,
      DEACTIVATE_LAYER,
    ]),

    onLayerClick(layerId) {
      if (!layerId) { log.error('No layer ID.'); return; }
      const bcr = this.$refs.layer.getBoundingClientRect();
      this.SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT(bcr);
      this.ACTIVATE_LAYER_BY_ID(layerId);
    },

    /**
     * Called when there is a selected layer and user clicks outside,
     * deselects active layer then.
     * Uses shouldDeactivateLayer to check if this function is called or click is being
     * ignored (e.g. when clicking on layer settings)
     */
    onClickOutside() {
      if (!this.isActiveLayer || this.LAYOUT_MODE_ACTIVE) { return; }
      this.SET_ACTIVE_LAYER_BOUNDING_CLIENT_RECT_TO_NONE();
      this.DEACTIVATE_LAYER();
    },

    /**
     * This check should return false for all classes that
     * should not deactivate the selected layer.
     * For some elements checking the ancestors is also needed.
     */
    shouldDeactivateLayer(event) {
      // add layers here which should NOT result in layer-deselection
      const ignoreArr = [
        'kd-layer-settings',
      ];
      /* eslint-disable arrow-body-style */
      const containsClassName = ignoreArr.some((className) => {
        return className && className.indexOf(event.target.className) > -1;
      });
      const isLayer = this.isAncestorOf(event.target, 'kd-layer');
      return !(containsClassName || isLayer);
    },

    /**
     * Checks if element el has ancestors with class className
     */
    // isAncestorOf(el, className) {
    //   let element = el;
    //   while (element) {
    //     const parent = element.parentNode;
    //     if (parent && parent.className && parent.className.indexOf(className) > -1) {
    //       return true;
    //     }
    //     element = parent;
    //   }
    //   return false;
    // },
    isAncestorOf: domHelpers.isAncestorOf,
  },
  watch: {
    ACTIVE_LAYER_ID(newVal) {
      this.isActiveLayer = this.layer.id === newVal;
    },
  },
  components: {
    KdLayerText,
    KdLayerFoto,
    KdLayerGrafik,
    KdLayerUrl,
  },
};
</script>

<style lang="scss">
.kd-layer {
  position: absolute;

  &:hover,  &--is-active {
    outline: 2px solid #01A3E0;
  }

  // In Layout mode we don't want the outline
  &--no-highlight {
    outline: none !important;
  }
}
</style>
